/*
  Kkumi Greenlight — cart guard. A campaign pledge MUST check out on its own.

  Why: the pledge rides the cart as a `_greenlight=<slug>` line-item property
  that the rewards worker reconciles the campaign against (and price-checks).
  Shopify's express-checkout buttons (Buy it now, Shop Pay, PayPal) silently
  drop line properties, so a pledge sharing a cart with other items risks a paid
  order whose pledge doesn't count toward funding (and, if the campaign fails,
  the backer may not land in the refund cohort). There's no server-side checkout
  validation we can use (Shopify Functions need Plus), so this is the storefront
  enforcement, in two layers — backed by a worker-side merchant alert if a mixed
  order ever slips through (see apps/rewards/src/handlers/webhooks.js):

    1. Block the ADD at the point of intent, BOTH directions:
         - adding a pledge while the cart holds other items / another campaign,
         - adding a normal item while the cart already holds a pledge.
       (Front-loading the constraint beats greying out the checkout button after
       the customer has committed.)
    2. Safety net on the cart page + drawer: if a mixed cart ever exists, disable
       the checkout button AND the dynamic-checkout buttons, with a note.

  Zero Liquid — pure static asset. Cart state is read from /cart.js and kept
  fresh on the theme's `cart-update` pub/sub event. Messages are written with
  textContent / DOM APIs (never innerHTML).
*/
(function () {
  var GL = '_greenlight';
  var cart = null; // last known /cart.js snapshot (null = unknown yet)

  function root() {
    return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  }
  function cartUrl() { return root() + '/cart'; }

  // --- cart-state reads ------------------------------------------------------

  // The campaign slug on a cart line's `_greenlight` property, or null. /cart.js
  // exposes line properties as a plain object: item.properties._greenlight.
  function lineSlug(item) {
    var p = item && item.properties;
    if (p && typeof p === 'object' && p[GL]) return String(p[GL]);
    return null;
  }
  function pledgeSlugs(c) {
    var seen = {};
    ((c && c.items) || []).forEach(function (it) { var s = lineSlug(it); if (s) seen[s] = 1; });
    return Object.keys(seen);
  }
  function hasOtherItems(c) {
    return ((c && c.items) || []).some(function (it) { return !lineSlug(it); });
  }
  // Mixed = at least one pledge AND (a non-pledge item OR a second campaign).
  function isMixed(c) {
    var slugs = pledgeSlugs(c);
    return slugs.length > 0 && (hasOtherItems(c) || slugs.length > 1);
  }

  function refresh() {
    return fetch(root() + '/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) { cart = c; applyCartGuard(); } return cart; })
      .catch(function () { return cart; });
  }

  // --- layer 1: block the add ------------------------------------------------

  function isCartAddForm(form) {
    return (form.getAttribute('action') || '').toLowerCase().indexOf('/cart/add') !== -1;
  }
  function formSlug(form) {
    var input = form.querySelector('input[name="properties[' + GL + ']"]');
    return input ? String(input.value || '') : null;
  }

  // The block message for this add against the cached cart, or null to allow.
  function addConflict(form) {
    if (!cart) return null; // cart unknown — fail open (cart safety net + worker alert backstop)
    var slug = formSlug(form);
    var slugs = pledgeSlugs(cart);
    if (slug) {
      // Adding a pledge: allowed only if the cart is empty or already holds ONLY
      // this campaign's pledge(s).
      var clean = !hasOtherItems(cart) && slugs.every(function (s) { return s === slug; });
      return clean ? null : 'Backing a campaign checks out on its own. Finish or clear your cart first.';
    }
    // Adding a normal item: blocked if a pledge is already in the cart.
    if (slugs.length > 0) return 'You’ve got a campaign pledge in your cart; it checks out separately. Back it now, or remove it to add other items.';
    return null;
  }

  // A note that names WHY and offers the resolving action (the cart). Reused for
  // both the add-block and the cart safety net.
  function noteEl(message, withCartLink) {
    var p = document.createElement('p');
    p.className = 'kkumi-gl-guard__note';
    p.setAttribute('role', 'alert');
    p.appendChild(document.createTextNode(withCartLink ? message + ' ' : message));
    if (withCartLink) {
      var a = document.createElement('a');
      a.href = cartUrl();
      a.textContent = 'View cart';
      p.appendChild(a);
    }
    return p;
  }

  function showAddNote(form, message) {
    var host = form.parentNode;
    if (!host) return;
    var existing = host.querySelector('.kkumi-gl-guard__note');
    if (existing) existing.remove();
    host.insertBefore(noteEl(message, true), form.nextSibling);
  }

  // Capture phase so we run before the form's own submit handler (Dawn's
  // product-form AJAX add) and can stop it cleanly.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM' || !isCartAddForm(form)) return;
    var message = addConflict(form);
    if (!message) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    showAddNote(form, message);
  }, true);

  // --- layer 2: cart page + drawer safety net --------------------------------

  function applyCartGuard() {
    var mixed = !!(cart && isMixed(cart));

    // Checkout buttons: only ever re-enable ones WE disabled (tracked via
    // data-gl-disabled) so we never fight Dawn's empty-cart disable.
    var buttons = document.querySelectorAll('#checkout, #CartDrawer-Checkout');
    [].forEach.call(buttons, function (btn) {
      if (mixed) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.dataset.glDisabled = '1';
      } else if (btn.dataset.glDisabled) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        delete btn.dataset.glDisabled;
      }
    });

    // Dynamic / express checkout buttons — the very surface that would drop the
    // pledge property. Hide the whole container when mixed.
    var dyn = document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons');
    [].forEach.call(dyn, function (d) { d.hidden = mixed; });

    // Explainer beside each checkout group.
    var hosts = document.querySelectorAll('.kkumi-summary__checkout, .cart__ctas');
    [].forEach.call(hosts, function (hostEl) {
      var note = hostEl.querySelector('.kkumi-gl-guard__note');
      if (!mixed) { if (note) note.remove(); return; }
      if (note) return; // already shown
      hostEl.insertBefore(
        noteEl('A campaign pledge has to check out on its own. Remove the other items (or the pledge) to continue.', false),
        hostEl.firstChild
      );
    });
  }

  // --- init + refresh triggers ----------------------------------------------

  function init() {
    refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
      // Cart sections re-render on every cart change, wiping our note/attrs — re-apply.
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refresh(); });
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
